/**
 * @file     OwnerEntryCard.tsx
 * @purpose  The other doors into owner listings (design 10d), kind-agnostic:
 *           a card for Owner Home and the /deal-finder/landlord result that
 *           sends the owner to the front door; and "Continue your listing"
 *           wherever an unfinished draft exists. Both hide when the owner
 *           listings flag is off.
 */
'use client';

import '@/core/i18n/deferred/listings';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { featureFlags } from '@/core/config/featureFlags';
import { getListingKind, isListingKind } from '@/core/config/listingKinds';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { useAuth } from '@/hooks/useAuth';
import type { ListingSubmissionRow } from '@/services/listing-submissions/listingSubmissionModel';
import { listingApi } from '../api';
import { OWNER_LISTING_EVENTS, useOwnerListingAnalytics } from '../analytics';
import { listingAddressLabel } from '../labels';
import { HomeIcon, OwnerButton, hairline, hubColors, radius } from '../ui';
import { builderHref } from './startListing';

export type OwnerEntrySurface = 'owner_home' | 'landlord_funnel' | 'landing';

const COPY: Record<Exclude<OwnerEntrySurface, 'landing'>, { title: string; line: string }> = {
  owner_home: { title: 'listings.owner.entry.ownerHomeTitle', line: 'listings.owner.entry.ownerHomeLine' },
  landlord_funnel: { title: 'listings.owner.entry.landlordTitle', line: 'listings.owner.entry.landlordLine' },
};

export function OwnerEntryCard({ surface, kind = 'lease' }: { surface: Exclude<OwnerEntrySurface, 'landing'>; kind?: string }) {
  const { t } = useTranslation();
  const { track } = useOwnerListingAnalytics();
  if (!featureFlags.ownerListings || !isListingKind(kind)) return null;
  const copy = COPY[surface];
  return (
    <Box component="section" aria-labelledby={`owner-entry-${surface}`} sx={{ border: hairline, borderRadius: `${radius.lg}px`, backgroundColor: hubColors.surfaceCard, p: 5, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
      <Box component="span" aria-hidden sx={{ width: 40, height: 40, borderRadius: `${radius.sm}px`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: hubColors.chip, color: hubColors.chipText }}>
        <HomeIcon />
      </Box>
      <Box component="h3" id={`owner-entry-${surface}`} sx={{ m: 0, fontSize: 17, fontWeight: fontWeightTokens.semibold }}>{t(copy.title)}</Box>
      <Box component="p" sx={{ m: 0, fontSize: 14, lineHeight: 1.55, color: hubColors.textSecondary }}>{t(copy.line)}</Box>
      <OwnerButton
        size="sm"
        href="/rent-out-your-place"
        onClick={() => track(OWNER_LISTING_EVENTS.ownerEntryClick, { surface }, kind)}
        sx={{ mt: 2 }}
      >
        {t('listings.owner.entry.cta')}
      </OwnerButton>
    </Box>
  );
}

/** The most recent unfinished draft, or null. Signed-in owners only. */
export function useOpenDraft(): ListingSubmissionRow | null {
  const { isAuthenticated } = useAuth();
  const [draft, setDraft] = useState<ListingSubmissionRow | null>(null);
  useEffect(() => {
    if (!featureFlags.ownerListings || !isAuthenticated) return;
    let live = true;
    listingApi
      .listDrafts()
      .then(({ listings }) => {
        if (live) setDraft(listings.find((l) => l.status === 'draft' || l.status === 'changes_requested') ?? null);
      })
      .catch((err: unknown) => reportSilentFailure(err, { area: 'owner-listings/open-draft' }));
    return () => {
      live = false;
    };
  }, [isAuthenticated]);
  return draft;
}

export function ContinueListingCard({ surface }: { surface: OwnerEntrySurface }) {
  const { t } = useTranslation();
  const { track } = useOwnerListingAnalytics();
  const draft = useOpenDraft();
  if (!draft) return null;
  const address = listingAddressLabel(draft.address) || t('listings.owner.dashboard.untitled');
  const kindLabel = isListingKind(draft.kind) ? t(getListingKind(draft.kind).labelKey) : '';
  return (
    <Box component="section" aria-labelledby="owner-continue-title" sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', border: hairline, borderRadius: `${radius.lg}px`, backgroundColor: hubColors.surfaceCard, px: 5, py: 4 }}>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Box component="p" id="owner-continue-title" sx={{ m: 0, fontSize: 15, fontWeight: fontWeightTokens.semibold }}>{t('listings.owner.door.continueTitle')}</Box>
        <Box component="p" sx={{ m: 0, mt: 0.5, fontSize: 13.5, color: hubColors.textSecondary }}>
          {t('listings.owner.door.continueMeta', { address, pct: draft.completeness })}
          {kindLabel ? ` · ${kindLabel}` : ''}
        </Box>
      </Box>
      <OwnerButton
        size="sm"
        href={builderHref(draft.id, surface)}
        onClick={() => track(OWNER_LISTING_EVENTS.draftResumed, { surface }, draft.kind)}
      >
        {t('listings.owner.door.continueCta')}
      </OwnerButton>
    </Box>
  );
}
